import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2, Sparkles, Mail } from 'lucide-react'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import toast from 'react-hot-toast'

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Подтверждаем email...')
  const [resendEmail, setResendEmail] = useState(searchParams.get('email') || '')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const navigate = useNavigate()
  const { setAuthData, user: authUser } = useAuthStore()

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    if (!tokenParam) {
      setStatus('error')
      setMessage('Неверная ссылка подтверждения')
      return
    }

    client.get('/auth/verify-email', { params: { token: tokenParam } })
      .then(({ data }) => {
        if (data.access_token) {
          setAuthData(data.access_token, data.refresh_token, data.user)
        }

        if (data.already_verified) {
          setStatus('success')
          setMessage('Email уже был подтверждён ранее')
        } else {
          setStatus('success')
          setMessage('Email успешно подтверждён! 🎉')
        }
      })
      .catch((err) => {
        setStatus('error')
        setMessage(err?.response?.data?.detail || 'Ошибка подтверждения email')
      })
  }, [searchParams, setAuthData])

  const handleResendVerification = async () => {
    const email = resendEmail || authUser?.email
    if (!email) {
      toast.error('Укажите ваш email')
      return
    }
    if (!email.includes('@')) {
      toast.error('Введите корректный email')
      return
    }
    try {
      setSending(true)
      await client.post('/auth/resend-verification', { email })
      setSent(true)
      toast.success('Новое письмо отправлено! Проверьте почту.')
    } catch {
      toast.error('Ошибка при отправке письма')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f0f1a]">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-indigo-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">Chat-Neuro.ru</span>
          </Link>
        </div>

        <div className="glass-card p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-500/10 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">Подтверждение email</h1>
              <p className="text-sm text-gray-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">Email подтверждён!</h1>
              <p className="text-sm text-gray-400 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/chat')}
                  className="block w-full gradient-btn py-3 rounded-xl text-sm font-medium cursor-pointer"
                >
                  Перейти в чат
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="block w-full py-3 rounded-xl text-sm text-gray-400 hover:text-white transition-colors border border-white/10 cursor-pointer"
                >
                  Профиль
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">Ошибка</h1>
              <p className="text-sm text-red-400 mb-6">{message}</p>

              {!sent ? (
                <div className="space-y-3">
                  <input
                    type="email"
                    placeholder="Ваш email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="input-field text-sm w-full text-center"
                  />
                  <button
                    onClick={handleResendVerification}
                    disabled={sending || !resendEmail}
                    className="block w-full gradient-btn py-3 rounded-xl text-sm font-medium cursor-pointer disabled:opacity-40"
                  >
                    {sending ? 'Отправка...' : 'Запросить новое письмо'}
                  </button>
                  <Link
                    to="/login"
                    className="block w-full py-3 rounded-xl text-sm text-gray-400 hover:text-white transition-colors border border-white/10"
                  >
                    Войти
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm mb-3">
                    <Mail className="w-4 h-4" />
                    Письмо отправлено! Проверьте почту
                  </div>
                  <Link
                    to="/login"
                    className="block w-full gradient-btn py-3 rounded-xl text-sm font-medium"
                  >
                    Войти
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
